import { Loader2, ShieldCheck, ShieldX } from 'lucide-react';
import { useState } from 'react';
import type {
  BloqueioEntregador,
  BloqueioOrigem,
  BloqueioStatusFiltro,
  BloqueiosListParams,
} from '../../../../api/services/romaneioService';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent } from '../../../../components/ui/card';
import { ConfirmDialog } from '../../../../components/ui/confirm-dialog';
import { Input } from '../../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../../components/ui/tooltip';
import { useAuth } from '../../../../auth/AuthContext';
import { useOps } from '../../../../hooks/useOps';
import {
  AVISO_GESTOR,
  formatDataHora,
  formatDocumento,
  rotuloMotivo,
} from './bloqueio-utils';
import { BloquearDialog } from './bloquear-dialog';
import {
  mensagemErro,
  useBloquearEntregadores,
  useBloqueios,
} from './use-bloqueios';

const TODAS = '__todas__';

// Aba "Bloqueios" das telas de entregadores: lista os bloqueios da ops (do
// cliente e do gestor), bloqueia/desbloqueia em massa e desbloqueia por linha.
// Bloqueio do gestor aparece, mas só o gestor desfaz.
export function BloqueiosTab() {
  const { currentOps } = useOps();
  const { hasPermission } = useAuth();
  const podeAlterar = hasPermission(
    'entregoCadastroBulkUpdate',
    currentOps || undefined
  );

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<
    NonNullable<BloqueiosListParams['filters']>
  >({
    status: 'vigente',
  });
  const [documento, setDocumento] = useState('');
  const [dialogo, setDialogo] = useState<'bloquear' | 'desbloquear' | null>(
    null
  );
  const [desbloqueando, setDesbloqueando] = useState<BloqueioEntregador | null>(
    null
  );
  const [mensagem, setMensagem] = useState<{
    tipo: 'ok' | 'erro';
    texto: string;
  } | null>(null);

  const { data, isFetching, error } = useBloqueios(page, filters);
  const mutation = useBloquearEntregadores();

  const mudarFiltro = (
    patch: Partial<NonNullable<BloqueiosListParams['filters']>>
  ) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const confirmarDesbloqueio = async () => {
    if (!desbloqueando) return;
    const doc = desbloqueando.numero_documento;
    try {
      const res = await mutation.mutateAsync({
        documentos: [doc],
        acao: 'desbloquear',
      });
      const aviso = res.avisos[0]?.reason;
      setMensagem(
        res.success.length
          ? {
              tipo: 'ok',
              texto: `${formatDocumento(doc)} desbloqueado.${aviso ? ` ${aviso}` : ''}`,
            }
          : {
              tipo: 'erro',
              texto: res.failed[0]?.reason ?? 'Não foi possível desbloquear.',
            }
      );
    } catch (e) {
      setMensagem({ tipo: 'erro', texto: mensagemErro(e) });
    }
  };

  const itens = data?.items ?? [];
  const pag = data?.pagination;

  return (
    <div className="space-y-5">
      <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              mudarFiltro({ documento: documento.trim() || undefined });
            }}
          >
            <Input
              aria-label="Filtrar por CPF/CNPJ"
              placeholder="CPF/CNPJ"
              value={documento}
              onChange={(e) => setDocumento(e.target.value)}
              className="w-48"
            />
            <Button type="submit" variant="outline">
              Filtrar
            </Button>
          </form>

          <Select
            value={filters.status ?? 'vigente'}
            onValueChange={(v) =>
              mudarFiltro({ status: v as BloqueioStatusFiltro })
            }
          >
            <SelectTrigger className="w-44" aria-label="Status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="vigente">Bloqueados agora</SelectItem>
              <SelectItem value="revogado">Desbloqueados</SelectItem>
              <SelectItem value="todos">Histórico completo</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.origem ?? TODAS}
            onValueChange={(v) =>
              mudarFiltro({
                origem: v === TODAS ? undefined : (v as BloqueioOrigem),
              })
            }
          >
            <SelectTrigger className="w-44" aria-label="Origem">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={TODAS}>Todas as origens</SelectItem>
              <SelectItem value="cliente">Minha operação</SelectItem>
              <SelectItem value="gestor">Gestor</SelectItem>
            </SelectContent>
          </Select>

          {podeAlterar && (
            <div className="ml-auto flex gap-2">
              <Button
                variant="outline"
                onClick={() => setDialogo('desbloquear')}
              >
                <ShieldCheck className="h-4 w-4" />
                Desbloquear em massa
              </Button>
              <Button
                variant="destructive"
                onClick={() => setDialogo('bloquear')}
              >
                <ShieldX className="h-4 w-4" />
                Bloquear entregadores
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {mensagem && (
        <p
          className={`text-sm ${mensagem.tipo === 'ok' ? 'text-green-700' : 'text-red-600'}`}
        >
          {mensagem.texto}
        </p>
      )}
      {error && <p className="text-sm text-red-600">{mensagemErro(error)}</p>}

      <Card className="bg-white border-[hsl(214,32%,91%)] shadow-sm">
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entregador</TableHead>
                  <TableHead>Bloqueado por</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Bloqueado em</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itens.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-[hsl(215,16%,47%)]"
                    >
                      {isFetching ? (
                        <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                      ) : (
                        'Nenhum bloqueio encontrado'
                      )}
                    </TableCell>
                  </TableRow>
                )}
                {itens.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell>
                      <div className="font-medium">
                        {formatDocumento(b.numero_documento)}
                      </div>
                      <div className="text-xs text-[hsl(215,16%,47%)]">
                        {b.nome_completo ?? '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {b.origem === 'gestor' ? (
                        <Badge className="bg-[hsl(210,35%,32%)] text-white">
                          Gestor
                        </Badge>
                      ) : (
                        <div>
                          <Badge variant="outline">Minha operação</Badge>
                          <div className="text-xs text-[hsl(215,16%,47%)]">
                            {b.criado_por}
                          </div>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[240px]">
                      {rotuloMotivo(
                        b.motivo_codigo,
                        b.motivo_texto,
                        data?.motivos
                      )}
                    </TableCell>
                    <TableCell>{formatDataHora(b.criado_em)}</TableCell>
                    <TableCell>
                      {b.revogado_em ? (
                        <span className="text-xs text-[hsl(215,16%,47%)]">
                          Desbloqueado em {formatDataHora(b.revogado_em)}
                          {b.revogado_origem === 'gestor'
                            ? ' pelo gestor'
                            : b.revogado_por
                              ? ` por ${b.revogado_por}`
                              : ''}
                        </span>
                      ) : (
                        <Badge variant="destructive">Bloqueado</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {!b.revogado_em && podeAlterar && (
                        <AcaoDesbloquear
                          bloqueio={b}
                          carregando={
                            mutation.isPending && desbloqueando?.id === b.id
                          }
                          onClick={() => {
                            setMensagem(null);
                            setDesbloqueando(b);
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {pag && pag.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-end gap-2 text-sm">
              <span>
                Página {pag.page} de {pag.totalPages} ({pag.total})
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Anterior
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= pag.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Próxima
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <BloquearDialog
        acao={dialogo}
        motivos={data?.motivos ?? []}
        onClose={() => setDialogo(null)}
      />

      <ConfirmDialog
        open={!!desbloqueando}
        onOpenChange={(open) => !open && setDesbloqueando(null)}
        onConfirm={confirmarDesbloqueio}
        title="Desbloquear entregador?"
        description={
          desbloqueando
            ? `${formatDocumento(desbloqueando.numero_documento)} volta a receber ofertas nesta operação, a menos que tenha um bloqueio do gestor.`
            : undefined
        }
        confirmText="Desbloquear"
        cancelText="Cancelar"
      />
    </div>
  );
}

// Bloqueio do gestor: botão desabilitado com a orientação de falar com o gestor.
function AcaoDesbloquear({
  bloqueio,
  carregando,
  onClick,
}: {
  bloqueio: BloqueioEntregador;
  carregando: boolean;
  onClick: () => void;
}) {
  if (bloqueio.pode_desbloquear) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={onClick}
        disabled={carregando}
      >
        {carregando ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          'Desbloquear'
        )}
      </Button>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>
          <Button size="sm" variant="outline" disabled>
            Desbloquear
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{AVISO_GESTOR}</TooltipContent>
    </Tooltip>
  );
}
